/**
 * Organizations Hook
 * React hook for fetching internal and/or client organizations
 * (defaults to both; pass e.g. ['client'] to fetch only one list)
 * Uses smart caching to avoid duplicate API calls across components
 */

import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/lib/api'

export interface Organization {
  Id: number
  DisplayName: string
  Code: string
}

export type OrganizationType = 'internal' | 'client'

interface OrganizationsApiResponse {
  Items: Organization[]
}

interface CachedOrganizations {
  organizations: Organization[]
  loading: boolean
  error: string | null
  timestamp: number
}

interface UseOrganizationsReturn {
  // Loading states
  loadingInternal: boolean
  loadingClient: boolean
  loading: boolean // true if either is loading
  
  // Error states
  errorInternal: string | null
  errorClient: string | null
  error: string | null // first error encountered
  
  // Data
  internalOrganizations: Organization[]
  clientOrganizations: Organization[]
  
  // Actions
  refreshInternal: () => Promise<void>
  refreshClient: () => Promise<void>
  refreshAll: () => Promise<void>
}

// Global cache outside the hook - shared across all instances
const organizationsCache = {
  internal: {
    organizations: [],
    loading: false,
    error: null,
    timestamp: 0
  } as CachedOrganizations,
  
  client: {
    organizations: [],
    loading: false,
    error: null,
    timestamp: 0
  } as CachedOrganizations
}

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000

// Set of subscribers for cache updates
const subscribers = new Set<() => void>()

// Notify all subscribers when cache updates
const notifySubscribers = () => {
  subscribers.forEach(callback => callback())
}

// Track pending requests to avoid duplicates
const pendingRequests = {
  internal: null as Promise<void> | null,
  client: null as Promise<void> | null
}

export function useOrganizations(types: OrganizationType[] = ['internal', 'client']): UseOrganizationsReturn {
  const { mainApi } = useApi()
  const [, forceUpdate] = useState(0)
  
  // Subscribe to cache updates
  useEffect(() => {
    const updateCallback = () => forceUpdate(prev => prev + 1)
    subscribers.add(updateCallback)
    
    return () => {
      subscribers.delete(updateCallback)
    }
  }, [])

  // Generic fetch function
  const fetchOrganizations = useCallback(async (type: 'internal' | 'client'): Promise<Organization[]> => {
    const roleType = type === 'internal' ? 'InternalRole' : 'ClientRole'
    const endpoint = `api/org/getListOrganizationsOriginalAndStatusIsAtiveOrPendingChangeInActive${roleType}`
    
    console.log(`Fetching ${type} organizations...`)
    
    const params = new URLSearchParams({
      '$select': 'Id,DisplayName,Code'
    })
    
    const response = await mainApi.get<OrganizationsApiResponse>(`${endpoint}?${params.toString()}`)
    
    const orgs = response?.Items || []
    console.log(`Loaded ${orgs.length} ${type} organizations`)
    return orgs
  }, [mainApi])

  // Generic cache and fetch function
  const fetchAndCache = useCallback(async (type: 'internal' | 'client') => {
    // Return existing pending request to avoid duplicates
    if (pendingRequests[type]) {
      console.log(`${type} organizations fetch already in progress, waiting...`)
      return pendingRequests[type]
    }

    console.log(`Starting ${type} organizations fetch...`)
    
    const cache = organizationsCache[type]
    
    // Set loading state in cache
    cache.loading = true
    cache.error = null
    notifySubscribers()
    
    // Create pending request
    pendingRequests[type] = (async () => {
      try {
        const organizations = await fetchOrganizations(type)
        
        // Cache the successful results
        cache.organizations = organizations
        cache.loading = false
        cache.error = null
        cache.timestamp = Date.now()
        
        console.log(`Cached ${type} organizations`)
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : `Failed to load ${type} organizations`

        // Cache the error state but keep any previously loaded list — a stale list
        // beats an empty one (consumers resolving names by Id would otherwise lose
        // their labels until a refetch succeeds)
        cache.loading = false
        cache.error = errorMessage
        cache.timestamp = Date.now()
        
        console.error(`Failed to load ${type} organizations:`, err)
      } finally {
        pendingRequests[type] = null
        notifySubscribers()
      }
    })()
    
    return pendingRequests[type]
  }, [fetchOrganizations])

  const refreshInternal = useCallback(async () => {
    await fetchAndCache('internal')
  }, [fetchAndCache])

  const refreshClient = useCallback(async () => {
    await fetchAndCache('client')
  }, [fetchAndCache])

  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchAndCache('internal'),
      fetchAndCache('client')
    ])
  }, [fetchAndCache])

  // Check if data needs to be fetched (only the requested types; joined so an
  // inline array literal at the call site doesn't retrigger the effect)
  const typesKey = types.join(',')
  useEffect(() => {
    const now = Date.now()

    // Filter against the cache keys: an empty types array joins/splits to [''],
    // and this also drops any bogus string a plain-JS caller sneaks past the type
    const requested = typesKey
      .split(',')
      .filter((t): t is OrganizationType =>
        Object.prototype.hasOwnProperty.call(organizationsCache, t)
      )
    requested.forEach(type => {
      const cache = organizationsCache[type]
      if (!cache.timestamp ||
          (now - cache.timestamp) > CACHE_TTL ||
          cache.error) {
        fetchAndCache(type)
      }
    })
  }, [fetchAndCache, typesKey])

  return {
    // Loading states
    loadingInternal: organizationsCache.internal.loading,
    loadingClient: organizationsCache.client.loading,
    loading: organizationsCache.internal.loading || organizationsCache.client.loading,
    
    // Error states
    errorInternal: organizationsCache.internal.error,
    errorClient: organizationsCache.client.error,
    error: organizationsCache.internal.error || organizationsCache.client.error,
    
    // Data
    internalOrganizations: organizationsCache.internal.organizations,
    clientOrganizations: organizationsCache.client.organizations,
    
    // Actions
    refreshInternal,
    refreshClient,
    refreshAll
  }
} 